<template>
  <div class="ci-root">
    <!-- Lovable / video-proof card (plain div — avoid Bootstrap b-card chrome) -->
    <div
      v-if="isLovable"
      ref="comment"
      class="comment-box comment-box--lovable"
      :class="{
        active: comment.selected || isHighlighted,
        resolved: !!comment.resolved,
      }"
      :data-comment-id="comment.id"
      @click="toggleComment"
    >
      <div class="ci-lovable">
        <div class="ci-lovable__row">
          <div class="ci-lovable__body">
            <div class="ci-lovable__meta">
              <span
                class="ci-lovable__dot"
                :style="{ backgroundColor: colorForUser(comment.user_id) }"
              />
              <span class="ci-lovable__name">{{ comment.user_name }}</span>
              <span
                v-b-tooltip.hover.viewport.noninteractive="{title: comment.date_time}"
                class="ci-lovable__ago"
              >
                <span v-if="comment.edited">edited </span>{{ comment.ago }}
              </span>
            </div>
            <div
              v-if="comment.view_mode || comment.is_previous_version"
              class="ci-lovable__view"
            >
              <template v-if="comment.view_mode">
                {{ comment.view_mode }}{{ String(comment.view_mode).includes('view') || /^\d/.test(comment.view_mode) ? '' : ' view' }}
              </template>
              <template v-if="comment.view_mode && comment.is_previous_version">
                ·
              </template>
              <template v-if="comment.is_previous_version">
                {{ comment.source_version_name }}
              </template>
            </div>
            <CommentInput
              v-if="isEditingThis"
              :editing-comment-id="comment.id"
              :edited-comment="comment.comment"
            />
            <div
              v-else
              class="ci-lovable__text"
            >
              <span
                v-if="comment.status === 'draft'"
                class="text-danger font-weight-bold"
              >Draft: </span>
              <RichTextDisplay
                class="ci-lovable__html"
                :html="comment.comment"
              />
              <div
                v-if="comment.status === 'approved' || comment.resolved"
                class="mt-1 mb-0"
              >
                <span
                  v-if="comment.status === 'approved'"
                  class="ci-lovable__status ci-lovable__status--ok"
                >
                  Approved
                </span>
                <span
                  v-if="comment.resolved && !comment.parent_id"
                  class="ci-lovable__status"
                >
                  Resolved
                </span>
              </div>
            </div>
            <div
              v-if="comment.video_url && !isEditingThis"
              class="video mt-3"
            >
              <video
                :id="`comment-video-${comment.id}`"
                width="200"
                controls
                :src="comment.video_url"
              />
            </div>
            <AttachmentChips
              download
              :references="commentAttachments"
            />
          </div>

          <div
            v-if="actions && !showCommentBox"
            class="ci-lovable__actions"
            @click.stop
          >
            <button
              v-b-tooltip.hover.viewport.noninteractive="'Reply'"
              type="button"
              class="ci-lovable__btn"
              aria-label="Reply"
              title="Reply"
              @click="replyToComment"
            >
              <Undo2
                :size="24"
                :stroke-width="2"
                class="vp-i"
              />
            </button>
            <button
              v-if="comment.editable && !comment.resolved"
              v-b-tooltip.hover.viewport.noninteractive="'Edit'"
              type="button"
              class="ci-lovable__btn"
              aria-label="Edit"
              title="Edit"
              @click="editComment"
            >
              <Pencil
                :size="24"
                :stroke-width="2"
                class="vp-i"
              />
            </button>
            <button
              v-if="comment.status !== 'approved' && comment.status !== 'draft' && !comment.parent_id"
              v-b-tooltip.hover.viewport.noninteractive="'Approve'"
              type="button"
              class="ci-lovable__btn"
              aria-label="Approve"
              title="Approve"
              :disabled="comment.isApproving"
              @click="approveComment"
            >
              <Check
                :size="24"
                :stroke-width="2"
                class="vp-i"
              />
            </button>
            <button
              v-if="!comment.resolved && comment.status !== 'draft' && !comment.parent_id"
              v-b-tooltip.hover.viewport.noninteractive="'Resolve'"
              type="button"
              class="ci-lovable__btn"
              aria-label="Resolve"
              title="Resolve"
              :disabled="comment.isResolving"
              @click="resolveComment"
            >
              <Redo2
                :size="24"
                :stroke-width="2"
                class="vp-i"
              />
            </button>
            <BtnDelete
              v-if="comment.deletable"
              variant="link"
              size="sm"
              button-class="ci-lovable__btn ci-lovable__btn--danger"
              class="delete-comment"
              confirmation-html="Are you sure you want to delete this comment?"
              @confirm="deleteComment"
            >
              <Trash2
                :size="24"
                :stroke-width="2"
                class="vp-i"
              />
            </BtnDelete>
          </div>
        </div>
      </div>

      <div
        v-if="comment.replies_recursive && comment.replies_recursive.length"
        class="ci-lovable__replies"
      >
        <CommentItem
          v-for="reply in comment.replies_recursive"
          :key="reply.id"
          :comment="reply"
          :scroll="false"
          :actions="actions"
          :compact-actions="compactActions"
          :variant="variant"
          :show-proof-link="showProofLink"
          :proof-share-link="proofShareLink"
        />
      </div>
    </div>

    <!-- Default layout -->
    <b-card
      v-else
      ref="comment"
      class="mb-2 comment-box"
      :class="{
        active: comment.selected || isHighlighted,
        resolved: !!comment.resolved,
      }"
      body-class="p-3"
      :data-comment-id="comment.id"
    >
      <div @click="toggleComment">
        <b-form-row>
          <b-col>
            <div class="d-inline-flex flex-column">
              <span class="font-weight-bold">
                <span
                  class="color-dot m-0"
                  :style="{ backgroundColor: colorForUser(comment.user_id) }"
                /> {{ comment.user_name }}
              </span>
              <span
                v-if="comment.view_mode || comment.is_previous_version"
                class="font-italic font-sm"
              >
                <template v-if="comment.view_mode">
                  {{ comment.view_mode }} view
                </template>
                <template v-if="comment.view_mode && comment.is_previous_version">
                  ·
                </template>
                <template v-if="comment.is_previous_version">
                  {{ comment.source_version_name }}
                </template>
              </span>
            </div>
            <span
              v-b-tooltip.hover.viewport.noninteractive="{title: comment.date_time}"
              class="text-nowrap text-muted font-sm "
            >
              <span v-if="comment.edited">edited</span>
              {{ comment.ago }}
            </span>
          </b-col>

          <b-col
            v-if="actions && !showCommentBox && compactActions"
            cols="auto"
            class="d-flex align-self-start align-items-end"
          >
            <button
              v-b-tooltip.hover.viewport.noninteractive="'Reply'"
              type="button"
              class="comment-action-btn"
              @click.stop="replyToComment"
            >
              <Reply class="comment-action-btn__icon" />
            </button>
            <button
              v-if="comment.editable && !comment.resolved"
              v-b-tooltip.hover.viewport.noninteractive="'Edit'"
              type="button"
              class="comment-action-btn"
              @click.stop="editComment"
            >
              <Pencil class="comment-action-btn__icon" />
            </button>
            <button
              v-if="!comment.resolved && comment.status !== 'draft' && !comment.parent_id"
              v-b-tooltip.hover.viewport.noninteractive="'Mark as updated'"
              type="button"
              class="comment-action-btn"
              :disabled="comment.isResolving"
              @click.stop="resolveComment"
            >
              <CircleCheck class="comment-action-btn__icon" />
            </button>
            <button
              v-if="comment.status !== 'approved' && comment.status !== 'draft' && !comment.parent_id"
              v-b-tooltip.hover.viewport.noninteractive="'Mark as approved'"
              type="button"
              class="comment-action-btn"
              :disabled="comment.isApproving"
              @click.stop="approveComment"
            >
              <BadgeCheck class="comment-action-btn__icon" />
            </button>
            <BtnDelete
              v-if="comment.deletable"
              variant="link"
              button-class="comment-action-btn comment-action-btn--delete p-0"
              class="delete-comment"
              confirmation-html="Are you sure you want to delete this comment?"
              @confirm="deleteComment"
            >
              <Trash2 class="comment-action-btn__icon" />
            </BtnDelete>
          </b-col>

          <b-col
            v-else-if="actions && !showCommentBox"
            cols="auto"
            class="d-flex align-self-start"
          >
            <b-button
              v-b-tooltip.hover.viewport.noninteractive="'Reply'"
              variant="outline-primary"
              class="rounded-circle d-flex p-1 mr-1"
              size="sm"
              @click.stop="replyToComment"
            >
              <b-icon icon="reply" />
            </b-button>
            <b-button
              v-if="comment.editable && !comment.resolved"
              v-b-tooltip.hover.viewport.noninteractive="'Edit'"
              variant="outline-secondary"
              class="rounded-circle d-flex p-1 mr-1"
              size="sm"
              @click.stop="editComment"
            >
              <b-icon icon="pencil" />
            </b-button>
            <b-button
              v-if="!comment.resolved && comment.status !== 'draft' && !comment.parent_id"
              v-b-tooltip.hover.viewport.noninteractive="'Mark as updated'"
              variant="outline-success"
              class="rounded-circle d-flex p-1 mr-1"
              size="sm"
              :disabled="comment.isResolving"
              @click.stop="resolveComment"
            >
              <b-icon
                icon="check"
                scale="1.5"
                :animation="comment.isResolving ? 'throb' : ''"
              />
            </b-button>
            <b-button
              v-if="comment.status !== 'approved' && comment.status !== 'draft' && !comment.parent_id"
              v-b-tooltip.hover.viewport.noninteractive="'Mark as approved'"
              variant="outline-success"
              class="rounded-circle d-flex p-1 mr-1"
              size="sm"
              :disabled="comment.isApproving"
              @click.stop="approveComment"
            >
              <b-icon
                icon="patch-check"
                :animation="comment.isApproving ? 'throb' : ''"
              />
            </b-button>
            <BtnDelete
              v-if="comment.deletable"
              variant="outline-danger"
              button-class="rounded-circle d-flex p-1"
              class="delete-comment"
              confirmation-html="Are you sure you want to delete this comment?"
              @confirm="deleteComment"
            />
          </b-col>

          <b-col
            cols="12"
          />
        </b-form-row>
        <CommentInput
          v-if="isEditingThis"
          class="mt-2"
          :editing-comment-id="comment.id"
          :edited-comment="comment.comment"
        />
        <div
          v-else
          class="mt-2"
        >
          <span
            v-if="comment.status === 'draft'"
            class="text-danger font-weight-bold"
          >Draft: </span>
          <RichTextDisplay
            class="card-text d-inline-block mb-1"
            :html="comment.comment"
          />

          <div
            v-if="comment.status === 'approved' || comment.resolved"
            class="mt-1 mb-0"
          >
            <span
              v-if="comment.status === 'approved'"
              class="badge badge-success badge-sm mr-1"
              style="font-size: 0.7rem; padding: 2px 6px;"
            >
              <i
                class="fa fa-check"
                style="font-size: 0.65rem;"
              /> Approved
            </span>
            <span
              v-if="comment.resolved && !comment.parent_id"
              class="badge badge-info badge-sm"
              style="font-size: 0.7rem; padding: 2px 6px;"
            >
              <i
                class="fa fa-check-circle"
                style="font-size: 0.65rem;"
              /> Resolved
            </span>
          </div>

          <div
            v-if="showProofLink && comment.options && proofShareLink"
            class="mt-2"
          >
            <small>
              <i class="fa fa-map-pin text-muted" />
              <a
                :href="getProofPageUrl()"
                target="_blank"
                class="text-primary"
              >
                View on proof page →
              </a>
            </small>
          </div>
        </div>
        <div
          v-if="comment.video_url && !isEditingThis"
          class="video mt-3"
        >
          <video
            :id="`comment-video-${comment.id}`"
            width="200"
            controls
            :src="comment.video_url"
          />
        </div>
        <AttachmentChips
          download
          :references="commentAttachments"
        />
      </div>
      <div
        v-if="comment.replies_recursive && comment.replies_recursive.length"
        class="mt-2"
      >
        <CommentItem
          v-for="reply in comment.replies_recursive"
          :key="reply.id"
          :comment="reply"
          :scroll="false"
          :actions="actions"
          :compact-actions="compactActions"
          :variant="variant"
          :show-proof-link="showProofLink"
          :proof-share-link="proofShareLink"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import { mapActions, mapMutations, mapGetters } from 'vuex'
import BtnDelete from '../atoms/BtnDelete.vue'
import RichTextDisplay from '@frontend/components/common/editors/RichTextDisplay.vue'
import AttachmentChips from '@frontend/components/common/atoms/AttachmentChips.vue'
import { sameOriginUrl } from '@frontend/utils/fileAttachments'
import CommentInput from './CommentInput.vue'
import { getUserColor } from '../../../utils/colors.js';
import {
  Reply,
  Pencil,
  CircleCheck,
  BadgeCheck,
  Trash2,
  Undo2,
  Redo2,
  Check,
} from 'lucide-vue'

export default {
  name: 'CommentItem',

  components: {
    BtnDelete,
    RichTextDisplay,
    AttachmentChips,
    CommentInput,
    Reply,
    Pencil,
    CircleCheck,
    BadgeCheck,
    Trash2,
    Undo2,
    Redo2,
    Check,
  },

  props: {
    comment: {
      type: Object,
      default: () => {},
    },
    scroll: {
      type: Boolean,
      default: true,
    },
    actions: {
      type: Boolean,
      default: true,
    },
    compactActions: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'lovable'].includes(v),
    },
    showProofLink: {
      type: Boolean,
      default: false,
    },
    proofShareLink: {
      type: String,
      default: null,
    },
    isHighlighted: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('project', [
      'showCommentBox',
      'commentEditing',
    ]),
    isEditingThis () {
      return this.commentEditing === this.comment.id
    },
    commentAttachments () {
      return (this.comment.attachments || []).map((item) => ({
        ...item,
        url: sameOriginUrl(item.url),
        thumbnail_url: sameOriginUrl(item.thumbnail_url),
      }))
    },
    isLovable() {
      return this.variant === 'lovable';
    },
  },

  mounted () {
    if (this.scroll) {
      this.$root.$on('scroll-to-comment', this.scrollToComment)
    }
  },

  beforeDestroy () {
    if (this.scroll) {
      this.$root.$off('scroll-to-comment')
    }
  },

  methods: {
    ...mapActions('media', [
      'resolveHistoriesProjectComment',
      'deleteHistoriesProjectComment',
      'updateHistoriesProjectComment',
    ]),
    ...mapMutations('media', [
      'UPDATE_HISTORIES_PROJECT_COMMENT',
    ]),
    ...mapActions('project', [
      'updateCommentBox',
      'updateCommentText',
      'updateCommentStatus',
      'updateCommentEditing',
      'updateCommentReplying',
    ]),
    colorForUser(userId) {
      return getUserColor(userId);
    },
    toggleComment () {
      // Skip Vuex toggle when in layout editor context (showProofLink=true)
      // The parent CommentsPanel handles selection via isHighlighted prop
      if (this.showProofLink) {
        return
      }

      this.UPDATE_HISTORIES_PROJECT_COMMENT({
        ...this.comment,
        selected: !this.comment.selected
      })

      this.$nextTick(() => {
        this.$root.$emit('toggle-comment', this.comment)
        this.$nextTick(() => {
          if (this.comment.selected && this.comment.options) {
            this.$root.$emit('scroll-to-pin', this.comment)
          }
        })
      })
    },
    async resolveComment () {
      try {
        await this.resolveHistoriesProjectComment(this.comment)
        // Emit event so parent components can reload
        this.$emit('resolved', this.comment.id)
      } catch (error) {
        console.error('Error resolving comment:', error)
      }
    },
    async approveComment () {
      // Optimistic UI update: show loading state on this comment
      this.UPDATE_HISTORIES_PROJECT_COMMENT({
        ...this.comment,
        isApproving: true,
      })
      try {
        await this.updateHistoriesProjectComment({ data: { status: 'approved' }, id: this.comment.id })
        // Persisted: set status to 'approved' so lists can hide it
        this.UPDATE_HISTORIES_PROJECT_COMMENT({
          ...this.comment,
          isApproving: false,
          status: 'approved',
        })
        // Emit event so parent components can reload
        this.$emit('approved', this.comment.id)
      } catch (e) {
        // Revert loading state on error
        this.UPDATE_HISTORIES_PROJECT_COMMENT({
          ...this.comment,
          isApproving: false,
        })
      }
    },
    replyToComment () {
      // Only call Vuex actions if we're on the proof page (not layout editor)
      if (!this.showProofLink) {
        this.updateCommentReplying(this.comment.id)
        this.updateCommentBox(true)
      }
      this.$emit('reply', this.comment.id)
    },
    editComment () {
      // Only call Vuex actions if we're on the proof page (not layout editor)
      if (!this.showProofLink) {
        this.updateCommentText(this.comment.comment)
        this.updateCommentStatus(this.comment.status)
        this.updateCommentEditing(this.comment.id)
        this.updateCommentBox(true)
      }
      this.$emit('commentEditorOpen', this.comment)
      this.$emit('edit', this.comment)
    },
    async deleteComment () {
      try {
        await this.deleteHistoriesProjectComment(this.comment)
        // Emit event so parent components can reload
        this.$emit('deleted', this.comment.id)
      } catch (error) {
        console.error('Error deleting comment:', error)
      }
    },
    scrollToComment (comment) {
      if (comment.id !== this.comment.id) {
        return
      }
      this.$refs.comment.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    },
    getProofPageUrl() {
      if (!this.proofShareLink) return '#'
      // Add comment highlight parameter to the URL
      return `/banner/group/share/${this.proofShareLink}?highlight_comment=${this.comment.id}`
    },
  }
}
</script>

<style scoped>
 .video {
   display: flex;
   justify-content: end;
 }

.card-text >>> img {
  max-width: 100%;
}

.card-text >>> p {
  margin-bottom: 0;
  line-height: 1.25;
}

 .color-dot {
   width: 10px;
   height: 10px;
   border-radius: 50%;
   display: inline-block;
   margin-right: 6px;
 }

.ci-lovable {
  padding: 0.75rem;
}

.ci-lovable__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.ci-lovable__body {
  min-width: 0;
  flex: 1;
}

.ci-lovable__meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.ci-lovable__dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  flex-shrink: 0;
}

.ci-lovable__name {
  font-weight: 500;
  color: hsl(222.2 84% 4.9%);
}

.ci-lovable__ago {
  font-size: 0.75rem;
  color: hsl(215.4 16.3% 46.9%);
  white-space: nowrap;
}

.ci-lovable__view {
  margin-top: 0.125rem;
  font-size: 0.75rem;
  font-style: italic;
  color: hsl(215.4 16.3% 46.9%);
}

.ci-lovable__text {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: hsl(222.2 84% 4.9%);
}

.ci-lovable__html >>> p {
  margin-bottom: 0;
  line-height: 1.4;
}

.ci-lovable__html >>> img {
  max-width: 100%;
}

.ci-lovable__actions {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.25rem;
}

.ci-lovable__actions >>> > div {
  display: inline-flex;
}

.ci-lovable__btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 1.75rem !important; /* h-7 w-7 */
  height: 1.75rem !important;
  min-width: 1.75rem !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 999px !important; /* rounded-full */
  border: 1px solid #10b981 !important; /* border-emerald-500 */
  background: transparent !important;
  color: #059669 !important; /* text-emerald-600 */
  box-shadow: none !important;
  line-height: 1 !important;
  text-decoration: none !important;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.ci-lovable__btn:hover,
.ci-lovable__btn:focus,
.ci-lovable__btn:active {
  background: rgba(16, 185, 129, 0.1) !important; /* hover:bg-emerald-500/10 */
  color: #059669 !important;
  text-decoration: none !important;
  box-shadow: none !important;
}

.ci-lovable__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.ci-lovable__btn--danger {
  border-color: #f43f5e !important; /* border-rose-500 */
  color: #f43f5e !important; /* text-rose-500 */
}

.ci-lovable__btn--danger:hover,
.ci-lovable__btn--danger:focus,
.ci-lovable__btn--danger:active {
  background: rgba(244, 63, 94, 0.1) !important; /* hover:bg-rose-500/10 */
  color: #f43f5e !important;
}

/* Lucide icons keep viewBox 24 + stroke-width 2, display size scaled like Tailwind h-3.5 */
.vp-i,
.ci-lovable__actions >>> .vp-i,
.ci-lovable__btn >>> svg {
  width: 0.875rem !important;
  height: 0.875rem !important;
  flex-shrink: 0;
  display: block;
}

.ci-lovable__status {
  display: inline-block;
  margin-right: 0.25rem;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.7rem;
  font-weight: 500;
  background: hsl(210 40% 96.1%);
  color: hsl(215.4 16.3% 46.9%);
}

.ci-lovable__status--ok {
  background: rgba(16, 185, 129, 0.12);
  color: #047857;
}

.ci-lovable__replies {
  padding: 0 0.75rem 0.75rem;
  margin-top: 0 !important;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>

<style>
/* Hover and selection styles for clickable comments (unscoped to apply to b-card) */
.comment-box {
  cursor: pointer;
  transition: all 0.2s ease;
}

.comment-box:hover {
  background-color: rgba(26, 188, 156, 0.08);
  border-color: rgba(26, 188, 156, 0.3);
}

.comment-box.active {
  border: 1px solid rgba(26, 188, 156, 0.5) !important;
  background-color: rgba(26, 188, 156, 0.05) !important;
}

.comment-box--lovable.comment-box {
  margin-bottom: 0 !important;
  border: 1px solid hsl(214.3 31.8% 91.4%) !important;
  border-radius: 0.25rem !important;
  background: #fff !important;
  box-shadow: none !important;
  overflow: hidden;
}

.comment-box--lovable.comment-box:hover {
  background: hsl(210 40% 98%) !important;
  border-color: hsl(214.3 31.8% 91.4%) !important;
}

.comment-box--lovable.comment-box.active {
  border-color: rgba(5, 150, 105, 0.45) !important;
  background: rgba(5, 150, 105, 0.04) !important;
}

.ci-root {
  width: 100%;
}

.comment-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin-right: 2px;
  background: none;
  border: none;
  border-radius: 4px;
  color: #6c757d;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.comment-action-btn:hover {
  background: rgba(0, 0, 0, 0.07);
  color: #343a40;
}

.comment-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.comment-action-btn__icon {
  width: 14px;
  height: 14px;
}

.comment-action-btn--delete {
  color: #dc3545;
}

.comment-action-btn--delete:hover {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}
</style>
